/**
 * Editor actions with no dedicated `useX` hook home in hooks.ts (multipart
 * upload, a blob download, or a shape none of `useMutation`'s callers need):
 * AI enqueue, bulk mark-reviewed, import and export. Every call here hits a
 * real, documented endpoint directly — no mock fallback. A missing or
 * renamed endpoint must surface as a thrown ApiError, not a fabricated
 * success.
 */
import { api, query, request } from './client'

export function aiTranslate(
  projectId: string,
  body: { locales: string[]; key_ids?: string[]; only_untranslated: boolean },
): Promise<{ enqueued: number; skipped_manual: number }> {
  return api(`/api/projects/${projectId}/ai-translate`, { method: 'POST', body })
}

export function retranslate(body: {
  project_id: string
  key_id: string
  locale: string
}): Promise<{ enqueued: number; force: boolean }> {
  return api('/api/translations/retranslate', { method: 'POST', body })
}

export function markReviewed(translationIds: string[]): Promise<{ updated: number }> {
  return api('/api/translations/mark-reviewed', {
    method: 'POST',
    body: { translation_ids: translationIds },
  })
}

export function bulkDeprecateKeys(
  platform: string,
  body: { key_ids: string[]; deprecated: boolean },
): Promise<{ deprecated: boolean; affected: number }> {
  return api(`/api/platforms/${platform}/keys/bulk-deprecate`, { method: 'POST', body })
}

// --------------------------------------------------------------------------
// Import
// --------------------------------------------------------------------------

export interface ImportPreview {
  format: string
  created: number
  updated: number
  unchanged: number
  samples: { key: string; old_value: string | null; new_value: string }[]
}

export function importFile(args: {
  platform: string
  projectId: string
  locale: string
  file: File
  dryRun: boolean
}): Promise<ImportPreview> {
  const form = new FormData()
  form.set('platform', args.platform)
  form.set('project_id', args.projectId)
  form.set('locale', args.locale)
  form.set('dry_run', args.dryRun ? '1' : '0')
  form.set('file', args.file)
  return api<ImportPreview>('/api/import', { method: 'POST', form })
}

// --------------------------------------------------------------------------
// Export
// --------------------------------------------------------------------------

export async function exportFile(args: {
  platform: string
  clientSlug: string | null
  locale: string
  format: string
}): Promise<{ body: string; filename: string }> {
  const res = await request(
    `/api/export${query({
      platform: args.platform,
      locale: args.locale,
      format: args.format,
      client: args.clientSlug,
    })}`,
  )
  const disposition = res.headers.get('content-disposition') ?? ''
  const match = /filename="?([^";]+)"?/.exec(disposition)
  return {
    body: await res.text(),
    filename: match?.[1] ?? `${args.platform}.${args.locale}.${args.format}`,
  }
}
